import React from 'react';
import CalendarIcon from '@assets/icons/calendar.svg';
import { EVENT_SOURCES, EVENT_TYPES } from '@constants/eventTypes.ts';
import { useAppSelector } from '@hooks/reduxHooks.ts';
import { useLingui } from '@lingui/react/macro';
import { getUserPreferencesOrDefaults } from '@routes/___shared___/utils.ts';
import { isNotificationNew } from '@routes/sidebar/notifications/___shared___/helpers.ts';
import type { NotificationDetails } from '@routes/sidebar/notifications/api/notifications.api.ts';
import {
    CardInfoAssesseeName,
    CardInfoMetaText,
    CardInfoTopBar,
    CardInfoTopBarPills,
    CardInfoTopic,
    CardInfoTopicCalendar,
    CardInfoTopicCalendarIco,
    CardInfoTopicCalendarRow,
    CardInfoTopicCalendarText,
    CardInfoTopicText,
    CardInfoTopicWrapper,
    CardInfoWrapper,
    SystemSourceDescription,
} from '@routes/sidebar/notifications/ui/card-info/styled.tsx';
import Dates from '@routes/sidebar/notifications/ui/card-info-dates';
import { PillTag, PillTagWithIcon } from '@routes/sidebar/notifications/ui/tag-pill';
import { formatDateBasedOnPreferences as formatDate } from '@utils/date.ts';
import { isEmpty } from 'lodash';
interface Props {
    note: NotificationDetails;
    isNotifyHidden: boolean;
}

const CardInfo: React.FC<Props> = ({ note, isNotifyHidden }) => {
    const userInfo = useAppSelector((state) => state.user.userInfo);
    const userPreferencesSettings = getUserPreferencesOrDefaults(userInfo);
    const { t } = useLingui();
    const { preferredDateFormat: dateFormat } = userPreferencesSettings;
    const notificationType = note.notificationType;
    const notificationSource = note.notificationSource;

    const isSystemSourceType = note.notificationSource === EVENT_SOURCES.SYSTEM;
    const showPathwayName = !isEmpty(note.pathwayName) && note.pathwayName !== note.topic;

    const showDueDate = !(
        notificationType === EVENT_TYPES.COMPLETED ||
        notificationType === EVENT_TYPES.PASSED ||
        notificationType === EVENT_TYPES.INFO
    );
    const statusDate = formatDate(showDueDate ? note.dueDateUTC : note.createdUTC, dateFormat);

    return (
        <CardInfoWrapper>
            <CardInfoTopBar>
                <CardInfoTopBarPills>
                    <PillTag type={notificationType} source={notificationSource} />
                    <PillTagWithIcon type={notificationType} source={notificationSource} />
                    {isNotificationNew(note) && <PillTag source={'New'} type={''} />}
                </CardInfoTopBarPills>
            </CardInfoTopBar>
            <CardInfoTopic>
                <CardInfoTopicWrapper>
                    <CardInfoTopicText>{note.topic}</CardInfoTopicText>
                    {showPathwayName ? <CardInfoMetaText>{note.pathwayName}</CardInfoMetaText> : null}
                </CardInfoTopicWrapper>
                {isSystemSourceType ? (
                    <SystemSourceDescription>{note.description}</SystemSourceDescription>
                ) : (
                    <CardInfoTopicCalendarRow $isExpanded={!isNotifyHidden}>
                        <CardInfoTopicCalendar
                            $isExpanded={!isNotifyHidden}
                            $type={notificationType}
                            $source={notificationSource}
                        >
                            <CardInfoTopicCalendarIco>
                                <CalendarIcon />
                            </CardInfoTopicCalendarIco>
                            <CardInfoTopicCalendarText>
                                <Dates
                                    assignedDate={formatDate(note.createdUTC, dateFormat)}
                                    statusDate={statusDate}
                                    isOverdue={notificationType === EVENT_TYPES.OVERDUE}
                                    isCompleted={notificationType === EVENT_TYPES.COMPLETED}
                                    isPassed={notificationType === EVENT_TYPES.PASSED}
                                    dueDate={note.dueDateUTC}
                                />
                            </CardInfoTopicCalendarText>
                        </CardInfoTopicCalendar>
                        {!isEmpty(note.assesseeName) ? (
                            <CardInfoAssesseeName>{`| ${t`Assessee`}: ${note.assesseeName}`}</CardInfoAssesseeName>
                        ) : null}
                    </CardInfoTopicCalendarRow>
                )}
            </CardInfoTopic>
        </CardInfoWrapper>
    );
};
export default CardInfo;
